import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRealtime } from "@/lib/realtime";
import {
  Loader2, RefreshCw, Filter, X, Bus, User, AlertTriangle, Calendar,
  ChevronLeft, ChevronRight, Activity, Link as LinkIcon, Layers, Trash2,
  CheckCircle2, AlertCircle, Info, Zap, MapPin, Clock,
} from "lucide-react";

/**
 * Centre d'exploitation des services planifiés (Phase C).
 *
 * Vue tabulaire des planned_services (générés depuis les Tournées scolaires)
 * pour une plage de dates. Chaque ligne montre le véhicule + chauffeur
 * effectivement affecté (résolu via tour_assignments), les alertes
 * (capacité, GPS, GTFS) et les conflits (chevauchement horaire).
 *
 * Action principale: clic sur une ligne → dialogue d'affectation rapide
 * qui crée un OVERRIDE d'un jour dans tour_assignments. La ligne durable
 * reprend la main automatiquement le lendemain.
 */
function todayIso() { return new Date().toISOString().slice(0, 10); }
function addDaysIso(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function frDayLabel(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" });
}

const STATUS_FILTERS = [
  { key: "all", label: "Tous" },
  { key: "assigned", label: "Affectés" },
  { key: "unassigned", label: "Non affectés" },
  { key: "conflict", label: "Conflits" },
];

const ALERT_META = {
  no_assignment:      { color: "#DC2626", bg: "#FEE2E2", icon: AlertCircle },
  no_driver:          { color: "#CA8A04", bg: "#FEF9C3", icon: AlertTriangle },
  capacity_exceeded:  { color: "#EA580C", bg: "#FFEDD5", icon: AlertTriangle },
  vehicle_conflict:   { color: "#DC2626", bg: "#FEE2E2", icon: Zap },
  driver_conflict:    { color: "#DC2626", bg: "#FEE2E2", icon: Zap },
  gps_absent:         { color: "#0891B2", bg: "#CFFAFE", icon: Info },
  unknown_stops:      { color: "#7C3AED", bg: "#EDE9FE", icon: Info },
  location_exception: { color: "#7C3AED", bg: "#EDE9FE", icon: MapPin },
};

export default function Exploitation() {
  const [fromIso, setFromIso] = useState(todayIso());
  const [toIso, setToIso] = useState(todayIso());
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState({ total: 0, assigned: 0, unassigned: 0, with_alerts: 0, with_conflicts: 0, overrides: 0 });
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClient, setFilterClient] = useState("");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // { service }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_iso: fromIso, to_iso: toIso };
      if (filterClient) params.client_name = filterClient;
      if (filterStatus !== "all") params.status = filterStatus;
      const [psRes, vRes, dRes] = await Promise.all([
        api.get("/exploitation/planned-services", { params }),
        api.get("/vehicles"),
        api.get("/users?role=driver&active_only=true").catch(() => ({ data: [] })),
      ]);
      setServices(psRes.data?.items || []);
      setSummary(psRes.data?.summary || summary);
      const vList = Array.isArray(vRes.data) ? vRes.data : vRes.data?.items || [];
      setVehicles(vList);
      const dList = Array.isArray(dRes.data) ? dRes.data : dRes.data?.items || [];
      setDrivers(dList);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromIso, toIso, filterStatus, filterClient]);

  useEffect(() => { load(); }, [load]);

  // Realtime: any tour_assignment change refreshes the view
  useRealtime(["agenda", "exploitation"], {
    "assignment.created":        () => load(),
    "assignment.updated":        () => load(),
    "assignment.deleted":        () => load(),
    "assignment.moved":          () => load(),
    "assignment.driver_changed": () => load(),
    "assignment.upserted":       () => load(),
  });

  const clients = useMemo(() => {
    const set = new Set();
    services.forEach((s) => { if (s.client_name) set.add(s.client_name); });
    return Array.from(set).sort();
  }, [services]);

  const filteredServices = useMemo(() => {
    if (!search) return services;
    const s = search.toLowerCase();
    return services.filter((sv) => {
      const code = `${sv.service_code || ""}${sv.service_letter || ""}`.toLowerCase();
      return code.includes(s)
        || (sv.client_name || "").toLowerCase().includes(s)
        || (sv.effective_vehicle_label || "").toLowerCase().includes(s)
        || (sv.effective_driver_name || "").toLowerCase().includes(s);
    });
  }, [services, search]);

  const onPickToday   = () => { const t = todayIso(); setFromIso(t); setToIso(t); };
  const onPickWeek    = () => { const t = todayIso(); setFromIso(t); setToIso(addDaysIso(t, 6)); };
  const onPickPrevDay = () => { setFromIso(addDaysIso(fromIso, -1)); setToIso(addDaysIso(toIso, -1)); };
  const onPickNextDay = () => { setFromIso(addDaysIso(fromIso, 1));  setToIso(addDaysIso(toIso, 1));  };

  /** Redirects to the source-of-truth page with the corresponding tour
   *  pre-loaded in edit mode. Exploitation is read-only for assignments. */
  const onAssign = (service) => {
    const tourId = service.tour_id || service.template_day_id;
    if (tourId) {
      // If there's already a same-day override we open IT, else open the
      // override creation form (kind=override) for the tour.
      const kind = service.assignment_kind === "override" ? "override" : "override";
      window.location.href = `/admin/fleet-assignments?focus_tour=${tourId}&kind=${kind}`;
    } else {
      window.location.href = "/admin/fleet-assignments";
    }
  };
  const onRemoveOverride = (service) => {
    // Read-only: redirect to fleet-assignments where the user can remove
    // the override from the dialog (and the action is properly audited).
    const tourId = service.tour_id || service.template_day_id;
    if (tourId) {
      window.location.href = `/admin/fleet-assignments?focus_tour=${tourId}&kind=override`;
    } else {
      window.location.href = "/admin/fleet-assignments";
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] bg-zinc-100" data-testid="exploitation-page">
      {/* Header */}
      <header className="bg-zinc-900 text-white border-b-2 border-[#FFC60A] flex items-center px-3 py-2 gap-3 flex-shrink-0">
        <div className="flex items-center gap-2 mr-2">
          <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-[#FFC60A]">Exploitation · Phase C</div>
          <div className="font-display font-black text-lg tracking-tighter">SERVICES PLANIFIÉS</div>
        </div>
        <Kpi label="Total" value={summary.total} testId="exp-kpi-total" />
        <Kpi label="Affectés" value={summary.assigned} accent="#10B981" testId="exp-kpi-assigned" />
        <Kpi label="Non affectés" value={summary.unassigned} accent={summary.unassigned ? "#F59E0B" : null} testId="exp-kpi-unassigned" />
        <Kpi label="Avec alertes" value={summary.with_alerts} accent={summary.with_alerts ? "#F59E0B" : null} testId="exp-kpi-alerts" />
        <Kpi label="Conflits" value={summary.with_conflicts} accent={summary.with_conflicts ? "#DC2626" : null} testId="exp-kpi-conflicts" />
        <Kpi label="Overrides" value={summary.overrides} testId="exp-kpi-overrides" />
        <div className="flex-1" />
        <button onClick={load} disabled={loading} data-testid="exp-refresh"
          className="px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 inline-flex items-center gap-1 disabled:opacity-50">
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
      </header>

      {/* Control banner — Exploitation is the day-control dashboard, not
          the place to modify assignments. */}
      <div data-testid="exp-control-banner"
            className="bg-emerald-50 border-b border-emerald-300 px-4 py-2 flex items-center gap-3 text-sm flex-shrink-0">
        <CheckCircle2 size={14} className="text-emerald-700 shrink-0" />
        <div className="flex-1">
          <strong className="font-display font-black tracking-tight">Contrôle du jour</strong>
          <span className="text-zinc-700">
            {" "}— vérifiez ici l&apos;état des services et leurs alertes. Les modifications d&apos;affectation se font dans
            <a href="/admin/fleet-assignments" className="underline font-bold ml-1 hover:text-emerald-700">
              Affectations flotte
            </a>.
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-zinc-200 px-3 py-2 flex items-center gap-2 text-xs flex-wrap" data-testid="exp-toolbar">
        <button onClick={onPickPrevDay} data-testid="exp-prev-day"
          className="px-1.5 py-1 border border-zinc-300 hover:border-zinc-900"><ChevronLeft size={12} /></button>
        <input type="date" value={fromIso} onChange={(e) => setFromIso(e.target.value)} data-testid="exp-date-from"
          className="border border-zinc-300 px-2 py-1 font-mono" />
        <span className="text-zinc-400">→</span>
        <input type="date" value={toIso} onChange={(e) => setToIso(e.target.value)} data-testid="exp-date-to"
          className="border border-zinc-300 px-2 py-1 font-mono" />
        <button onClick={onPickNextDay} data-testid="exp-next-day"
          className="px-1.5 py-1 border border-zinc-300 hover:border-zinc-900"><ChevronRight size={12} /></button>
        <button onClick={onPickToday} data-testid="exp-pick-today"
          className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 text-[10px] font-bold uppercase tracking-widest">Aujourd&apos;hui</button>
        <button onClick={onPickWeek} data-testid="exp-pick-week"
          className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 text-[10px] font-bold uppercase tracking-widest">Semaine</button>

        <div className="w-px h-5 bg-zinc-200 mx-1" />

        <Filter size={12} className="text-zinc-500" />
        <div className="flex items-center gap-1" data-testid="exp-filter-status">
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilterStatus(f.key)} data-testid={`exp-filter-status-${f.key}`}
              className={`px-2 py-1 border text-[10px] font-bold uppercase tracking-widest ${filterStatus === f.key ? "border-zinc-900 bg-zinc-900 text-[#FFC60A]" : "border-zinc-300 hover:border-zinc-900"}`}>
              {f.label}
            </button>
          ))}
        </div>

        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} data-testid="exp-filter-client"
          className="border border-zinc-300 px-2 py-1 text-xs bg-white">
          <option value="">Tous clients</option>
          {clients.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Recherche code, client, bus, chauffeur…"
          data-testid="exp-search" className="border border-zinc-300 px-2 py-1 text-xs flex-1 min-w-[200px]" />
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3">
        {loading && services.length === 0 ? (
          <div className="text-center py-12 text-zinc-500"><Loader2 size={20} className="inline animate-spin mr-2" /> Chargement…</div>
        ) : filteredServices.length === 0 ? (
          <EmptyState fromIso={fromIso} toIso={toIso} />
        ) : (
          <ServicesTable
            services={filteredServices}
            onAssign={onAssign}
            onRemoveOverride={onRemoveOverride}
          />
        )}
      </div>

      {dialog && (
        <AssignDialog
          service={dialog.service}
          vehicles={vehicles}
          drivers={drivers}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div className="border border-zinc-700 bg-zinc-800/50 px-3 py-1.5 min-w-[80px]" data-testid={testId}>
      <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-zinc-400">{label}</div>
      <div className="font-display font-black text-xl tracking-tighter leading-none mt-0.5" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

function EmptyState({ fromIso, toIso }) {
  return (
    <div className="bg-white border border-zinc-200 p-8 text-center text-zinc-500" data-testid="exp-empty">
      <Calendar size={32} className="mx-auto mb-3 text-zinc-300" />
      <div className="font-display font-bold text-zinc-700">Aucun service planifié sur la période</div>
      <div className="text-xs mt-1 font-mono">
        {fromIso} → {toIso}
      </div>
      <div className="text-xs mt-3">
        Importez un programme et générez les services dans <strong>Tournées scolaires</strong>.
      </div>
    </div>
  );
}

function AssignmentPill({ kind }) {
  if (!kind) {
    return <span className="inline-block px-1.5 py-0.5 border border-zinc-200 bg-zinc-50 text-zinc-400 text-[9px] font-mono uppercase tracking-widest" data-testid="exp-pill-none">Non affecté</span>;
  }
  return kind === "override"
    ? <span className="inline-block px-1.5 py-0.5 border border-purple-300 bg-purple-50 text-purple-700 text-[9px] font-mono uppercase tracking-widest" data-testid="exp-pill-override">Override</span>
    : <span className="inline-block px-1.5 py-0.5 border border-sky-300 bg-sky-50 text-sky-700 text-[9px] font-mono uppercase tracking-widest" data-testid="exp-pill-durable">Durable</span>;
}

function AlertBadge({ alert, onClick }) {
  const meta = ALERT_META[alert.code] || { color: "#52525B", bg: "#F4F4F5", icon: Info };
  const Icon = meta.icon;
  const isLoc = alert.code === "location_exception";
  // Compact one-line tooltip combining lieu / horaire / consigne
  const tip = isLoc
    ? [
        alert.poi_name || alert.address,
        alert.all_day
          ? "Toute la journée"
          : alert.start_datetime
            ? `${String(alert.start_datetime).slice(11, 16)}–${String(alert.end_datetime || "").slice(11, 16)}`
            : "",
        alert.operational_notes,
      ].filter(Boolean).join(" · ")
    : alert.label;
  return (
    <button
      type="button"
      onClick={isLoc && onClick ? (e) => { e.stopPropagation(); onClick(alert); } : undefined}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-widest ${isLoc && onClick ? "cursor-pointer hover:shadow-sm" : "cursor-default"}`}
      title={tip}
      style={{ background: meta.bg, color: meta.color, borderColor: meta.color }}
      data-testid={`exp-alert-${alert.code}`}
    >
      <Icon size={9} />
      {isLoc ? (
        <span className="truncate max-w-[180px]">
          {(alert.poi_name || alert.address || "Lieu").slice(0, 28)}
        </span>
      ) : alert.code.replace(/_/g, " ")}
    </button>
  );
}

function LocationExceptionDialog({ alert, onClose }) {
  if (!alert) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4" data-testid="exp-loc-dialog">
      <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#A855F7] w-full max-w-lg">
        <header className="px-4 py-3 bg-purple-900 text-white flex items-center gap-3">
          <MapPin size={16} className="text-purple-200" />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-purple-200">Exception localisée</div>
            <div className="font-display font-black text-lg tracking-tighter truncate">
              {alert.poi_name || alert.address || "Lieu de l'exception"}
            </div>
          </div>
          <button onClick={onClose} className="text-purple-200 hover:text-white" data-testid="exp-loc-dialog-close">
            <X size={18} />
          </button>
        </header>
        <div className="p-4 space-y-3 text-sm" data-testid="exp-loc-dialog-body">
          {alert.address && alert.poi_name && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Adresse</div>
              <div>{alert.address}</div>
            </div>
          )}
          {(alert.latitude != null && alert.longitude != null) && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Coordonnées</div>
              <div className="font-mono text-xs">
                {Number(alert.latitude).toFixed(5)}, {Number(alert.longitude).toFixed(5)}
                <a
                  href={`https://www.openstreetmap.org/?mlat=${alert.latitude}&mlon=${alert.longitude}&zoom=17`}
                  target="_blank" rel="noreferrer"
                  className="ml-2 underline text-purple-700 hover:text-purple-900"
                  data-testid="exp-loc-dialog-osm-link"
                >Voir sur OSM ↗</a>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                <Clock size={10} className="inline mr-1" /> Début
              </div>
              <div className="font-mono text-xs">
                {alert.start_datetime ? String(alert.start_datetime).replace("T", " ").slice(0, 16) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                <Clock size={10} className="inline mr-1" /> Fin
              </div>
              <div className="font-mono text-xs">
                {alert.end_datetime ? String(alert.end_datetime).replace("T", " ").slice(0, 16) : "—"}
              </div>
            </div>
          </div>
          {alert.all_day && (
            <div className="text-[10px] font-mono uppercase tracking-widest bg-purple-50 border border-purple-200 px-2 py-1 inline-block">
              Toute la journée
            </div>
          )}
          {alert.operational_notes && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Consignes opérationnelles</div>
              <div className="bg-amber-50 border border-amber-300 px-3 py-2 whitespace-pre-wrap text-[13px]">
                {alert.operational_notes}
              </div>
            </div>
          )}
        </div>
        <footer className="border-t border-zinc-900 p-3 flex justify-end bg-zinc-50">
          <button
            onClick={onClose}
            data-testid="exp-loc-dialog-ok"
            className="px-3 py-1.5 bg-purple-600 text-white border border-purple-800 hover:bg-purple-700 text-[11px] font-black uppercase tracking-widest">
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
}

function ServicesTable({ services, onAssign, onRemoveOverride }) {
  // Group by date for visual separation
  const grouped = useMemo(() => {
    const out = {};
    services.forEach((s) => { (out[s.date] ||= []).push(s); });
    return Object.entries(out).sort(([a], [b]) => a.localeCompare(b));
  }, [services]);

  // Selected location-exception alert → shown in a modal
  const [locDialog, setLocDialog] = useState(null);

  return (
    <div className="space-y-4">
      {grouped.map(([date, rows]) => (
        <div key={date} className="bg-white border border-zinc-200" data-testid={`exp-day-${date}`}>
          <div className="bg-zinc-50 border-b border-zinc-200 px-3 py-1.5 flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-700 font-bold">
              {frDayLabel(date)} · <span className="text-zinc-400">{date}</span>
            </div>
            <div className="text-[10px] font-mono text-zinc-500">{rows.length} service{rows.length > 1 ? "s" : ""}</div>
          </div>
          <table className="w-full text-sm" data-testid="exp-table">
            <thead className="bg-white border-b border-zinc-200">
              <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <th className="text-left px-3 py-2">Service</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Horaire</th>
                <th className="text-left px-3 py-2">Capacité</th>
                <th className="text-left px-3 py-2">Bus</th>
                <th className="text-left px-3 py-2">Chauffeur</th>
                <th className="text-left px-3 py-2">Statut</th>
                <th className="text-left px-3 py-2">Alertes</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((s) => {
                const isConflict = s.has_conflict;
                const isOverride = s.assignment_kind === "override";
                return (
                  <tr key={s.id} data-testid={`exp-row-${s.id}`}
                    className={`hover:bg-zinc-50 cursor-pointer transition-colors ${isConflict ? "bg-red-50/40" : ""}`}
                    onClick={() => onAssign(s)}>
                    <td className="px-3 py-2 font-bold font-mono">
                      {s.service_code}{s.service_letter || ""}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-700">{s.client_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{s.start_time || "—"} → {s.end_time || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{s.vehicle_capacity || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      {s.effective_vehicle_label || <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {s.effective_driver_name || <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2"><AssignmentPill kind={s.assignment_kind} /></td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(s.alerts || []).map((a, idx) => (
                          <AlertBadge
                            key={`${s.id}-${a.code}-${idx}`}
                            alert={a}
                            onClick={a.code === "location_exception" ? setLocDialog : undefined}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); onAssign(s); }}
                        data-testid={`exp-row-assign-${s.id}`}
                        title="Ouvrir la fiche d'affectation dans Affectations flotte"
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-900 hover:text-[#FFC60A]">
                        <LinkIcon size={11} /> Modifier ↗
                      </button>
                      {isOverride && (
                        <button onClick={(e) => { e.stopPropagation(); onRemoveOverride(s); }}
                          data-testid={`exp-row-remove-override-${s.id}`}
                          title="Modifier l'exception dans Affectations flotte"
                          className="ml-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-1 border border-transparent text-zinc-500 hover:text-red-600 hover:border-red-200">
                          <Trash2 size={11} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      <LocationExceptionDialog alert={locDialog} onClose={() => setLocDialog(null)} />
    </div>
  );
}

function AssignDialog({ service, vehicles, drivers, onClose, onDone }) {
  const [vehicleId, setVehicleId] = useState(service.effective_vehicle_id || "");
  const [driverId, setDriverId]   = useState(service.effective_driver_id || "");
  const [notes, setNotes]         = useState("");
  const [saving, setSaving]       = useState(false);
  const code = `${service.service_code || ""}${service.service_letter || ""}`;

  const save = async () => {
    if (!vehicleId) { toast.error("Choisir un véhicule"); return; }
    setSaving(true);
    try {
      await api.post(`/exploitation/planned-services/${service.id}/assign`, {
        vehicle_id: vehicleId,
        driver_id: driverId || null,
        notes: notes || "",
      });
      toast.success(`Override créé pour ${code} le ${service.date}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  // Suggested vehicles: matching capacity > others
  const needsCap = (() => {
    const v = service.vehicle_capacity;
    if (typeof v === "string" && v.includes("+")) {
      const [a, b] = v.split("+").map((x) => parseInt(x.trim(), 10));
      return { std: a || 0, pmr: b || 0 };
    }
    if (v) return { std: parseInt(v, 10) || 0, pmr: 0 };
    return { std: 0, pmr: 0 };
  })();
  const veh = useMemo(() => {
    const decorated = vehicles.map((v) => {
      const cap = parseInt(v.capacity || 0, 10);
      const pmr = parseInt(v.pmr_seats || 0, 10);
      const ok = cap >= needsCap.std && pmr >= needsCap.pmr;
      const niceName = v.model || v.label || v.plate || v.license_plate || (v.id || "").slice(0, 8);
      return { ...v, _ok: ok, _name: niceName, _cap: cap, _pmr: pmr };
    });
    decorated.sort((a, b) => (b._ok - a._ok) || a._name.localeCompare(b._name));
    return decorated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, needsCap.std, needsCap.pmr]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose} data-testid="exp-dialog">
      <div className="bg-white border-2 border-zinc-900 w-[520px] max-w-[95vw]" onClick={(e) => e.stopPropagation()}>
        <div className="bg-zinc-900 text-white px-3 py-2 border-b-2 border-[#FFC60A] flex items-center justify-between">
          <div>
            <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-[#FFC60A]">Affectation override · 1 jour</div>
            <div className="font-display font-black text-lg tracking-tighter">{code} · {service.date}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="exp-dialog-close"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Client</div>
              <div className="font-bold">{service.client_name || "—"}</div>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Horaire</div>
              <div className="font-mono">{service.start_time} → {service.end_time}</div>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Capacité requise</div>
              <div className="font-mono">{service.vehicle_capacity || "—"}</div>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Affectation actuelle</div>
              <div className="font-bold">{service.effective_vehicle_label || "Aucune"}</div>
            </div>
          </div>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
              <Bus size={10} className="inline mr-1" /> Bus (override pour ce jour)
            </div>
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
              data-testid="exp-dialog-vehicle"
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white">
              <option value="">— Choisir un bus —</option>
              {veh.map((v) => (
                <option key={v.id} value={v.id}>
                  {v._name} · cap {v._cap || "?"}+{v._pmr || 0}{v._ok ? "" : " ⚠"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
              <User size={10} className="inline mr-1" /> Chauffeur (optionnel)
            </div>
            <select value={driverId} onChange={(e) => setDriverId(e.target.value)}
              data-testid="exp-dialog-driver"
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white">
              <option value="">— Aucun —</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Notes</div>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="exp-dialog-notes"
              placeholder="Ex. exception panne, conducteur en formation, …"
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm" />
          </div>
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-2 py-1.5">
            ⚠ Cette affectation est un <strong>override d&apos;un jour</strong>. L&apos;affectation durable de la
            tournée reprend automatiquement le lendemain.
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={onClose} data-testid="exp-dialog-cancel"
              className="px-3 py-1.5 border border-zinc-300 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-100">
              Annuler
            </button>
            <button onClick={save} disabled={saving || !vehicleId} data-testid="exp-dialog-save"
              className="px-3 py-1.5 bg-zinc-900 text-[#FFC60A] border-2 border-zinc-900 text-[10px] font-bold uppercase tracking-widest hover:bg-black disabled:opacity-50 inline-flex items-center gap-1">
              {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              Enregistrer override
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
